import { geocodeByAddress } from 'react-google-places-autocomplete';
import { AddressDetailState } from 'schemas/addressDetailsStateSchema';
import { GeoPoint } from 'schemas/geoPointSchema';
import { PanelLineState } from 'schemas/panelLineStateSchema';
import { PointB } from 'schemas/pointSchema';

// Constants for long/lat calculation
const earthRadius = 6378.137; // radius of the earth in kilometer;
const metersToLat = 1 / (((2 * Math.PI) / 360) * earthRadius) / 1000; // 1 meter in degree

/**
 * Transform meters to latitude degrees
 * @param meter
 * @returns
 */
const meterToLatitiude = (meter: number) => {
  const latitude = meter * metersToLat;
  return latitude;
};

/**
 * Transform meters to longitude degrees
 * @param meters
 * @param atLat
 * @returns
 */
const meterToLongitude = (meters: number, atLat: number) => {
  const longitude = (meters * metersToLat) / Math.cos(atLat * (Math.PI / 180));
  return longitude;
};

/**
 * Calculate distance between two points
 * @param point1
 * @param point2
 * @returns
 */
export const distanceBetweenPoints = (point1: PointB, point2: PointB) =>
  Math.hypot(point2.x - point1.x, point2.y - point1.y);

/**
 * Calculate angle between to points
 * @param p1
 * @param p2
 * @returns
 */
export const angleBetweenPoints = (p1: PointB, p2: PointB) => {
  // angle in radians
  const angleRadians = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  // angle in degrees
  const angleDeg = (angleRadians * 180) / Math.PI;
  return angleDeg;
};

// doc:
// https://gis.stackexchange.com/questions/7430/what-ratio-scales-do-google-maps-zoom-levels-correspond-to
export const metersPerPx = (zoomLevel: number, lat: number) =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoomLevel;

/**
 * Retrieve distance in km between to Geo location
 * @param cord1
 * @param cord2
 * @returns
 */
export function getDistanceBetweenTwoPoints(cord1: GeoPoint, cord2: GeoPoint) {
  if (cord1.lat === cord2.lat && cord1.lng === cord2.lng) {
    return 0;
  }

  const radlat1 = (Math.PI * cord1.lat) / 180;
  const radlat2 = (Math.PI * cord2.lat) / 180;

  const theta = cord1.lng - cord2.lng;
  const radtheta = (Math.PI * theta) / 180;

  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);

  if (dist > 1) {
    dist = 1;
  }

  dist = Math.acos(dist);
  dist = (dist * 180) / Math.PI;
  dist = dist * 60 * 1.1515;
  dist *= 1.609344; // convert miles to km

  return dist;
}

const delay = (n: number) =>
  new Promise((r) => {
    setTimeout(r, n * 1000);
  });

/**
 * Converts a geo code result from google to readable adress
 * @param result
 * @returns
 */
export const convertGeoCodeResultToAdressDetail = (
  result: google.maps.GeocoderResult[]
): AddressDetailState | undefined => {
  if (result && result.length > 0) {
    const countryNode = result[0].address_components.find(
      (c) => c.types[0] === 'country'
    );
    return {
      address: result[0].formatted_address,
      country: countryNode?.long_name || 'Belgique',
      countryCode: countryNode?.short_name || 'BE',
      postCode:
        result[0].address_components.find((c) => c.types[0] === 'postal_code')
          ?.long_name || '0',
      geoCode: {
        lat: result[0].geometry.location.lat(),
        lng: result[0].geometry.location.lng(),
      },
    };
  }

  return undefined;
};

/**
 * Retrieve address details using google map geocoding api
 * @param address
 * @returns
 */
export const getAddressDetails = async (
  address: string
): Promise<AddressDetailState> => {
  await delay(1);
  const result = await geocodeByAddress(address); // TODO: save the geocode in the inital screen, this will avoid multiple non desired calls...
  if (result && result.length > 0) {
    const convertedResult = convertGeoCodeResultToAdressDetail(result);
    if (convertedResult) {
      return convertedResult;
    }
  }
  throw Error(`Geo point not found for adress:${address}`);
};

export const getNumPanels = (
  panelLines: PanelLineState[] | undefined
): number => {
  let numPanels: number = 0;
  panelLines?.forEach((pLine) => {
    const num = pLine.panelAmount || 0;
    const hiddenInsideRange = pLine.hiddenPanels.filter(
      (pIndex) => pIndex < num
    ).length;
    numPanels = numPanels + num - hiddenInsideRange;
  });
  return numPanels < 0 ? 0 : numPanels;
};

/**
 * Retrieve the center point in GeoLocation of a panel Line
 * This allows to be "rencetered" directly on a panel line when opening the map.
 * @param initialLocation
 * @param zoomLevel
 * @param panelLine
 * @returns
 */
export const calculatePanelGeoPoint = (
  initialLocation: GeoPoint,
  zoomLevel: number,
  panelLine: PanelLineState
): GeoPoint => {
  const meterPerPixel = metersPerPx(zoomLevel, initialLocation.lat);
  const midpoint = {
    x: (panelLine.point1.x + panelLine.point2.x) / 2,
    y: (panelLine.point1.y + panelLine.point2.y) / 2,
  };

  const newLat =
    initialLocation.lat - midpoint.y * meterPerPixel * meterToLatitiude(1);
  const newLng =
    initialLocation.lng +
    midpoint.x * meterPerPixel * meterToLongitude(1, initialLocation.lat);

  return {
    lat: newLat,
    lng: newLng,
  };
};
